<template>
    <div class="modal-overlay" @click.self="close">
        <div class="modal-container">
            <button class="modal-close" @click="close" :title="$t('iconAlts.close')">
                <i class="icon icon-close" />
            </button>

            <div v-if="!submitted" class="modal-content">
                <div class="form-header">
                    <h2>{{ $t("support.title") }}</h2>
                </div>

                <form @submit.prevent="handleSubmit">
                    <div class="form-group">
                        <label for="email">{{ $t("support.email") }}</label>
                        <input
                            id="email"
                            v-model="form.email"
                            type="email"
                            :placeholder="$t('support.emailPlaceholder')"
                            required
                            :disabled="submitting"
                        />
                    </div>

                    <div class="form-group">
                        <label for="message">{{ $t("support.reportIssue") }}</label>
                        <textarea
                            id="message"
                            v-model="form.message"
                            rows="8"
                            :placeholder="$t('support.reportIssuePlaceholder')"
                            required
                            :disabled="submitting"
                        ></textarea>
                    </div>

                    <div class="form-actions">
                        <button type="submit" :disabled="submitting" class="btn">
                            <span v-if="!submitting">{{ $t("support.submit") }}</span>
                            <Spinner v-else />
                        </button>
                    </div>
                </form>
            </div>

            <div v-else class="result-screen">
                <div class="result-icon" :class="{ success: submissionSuccess, error: !submissionSuccess }">
                    <i v-if="submissionSuccess" class="icon icon-icon-success">
                        <span class="path1"></span>
                        <span class="path2"></span>
                    </i>
                    <i v-else class="icon icon-icon-warning-error">
                        <span class="path1"></span>
                        <span class="path2"></span>
                    </i>
                </div>
                <h3 class="result-title">
                    {{ submissionSuccess ? $t("support.issueSubmitted") : $t("support.submissionFailed") }}
                </h3>
                <p class="result-message" v-if="submissionSuccess" v-html="$t('support.thankYouMessage')"></p>
                <p class="result-message" v-else>{{ errorMessage }}</p>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import Vue from "vue";
import { deskProService } from "@/api/deskpro";
import Spinner from "./Spinner.vue";

interface FormData {
    email: string;
    message: string;
}

export default Vue.extend({
    name: "SupportModal",
    components: {
        Spinner,
    },
    props: {
        formData: {
            type: Object as () => FormData,
            required: true,
        },
    },
    data() {
        return {
            form: { ...this.formData } as FormData,
            submitting: false,
            submitted: false,
            submissionSuccess: false,
            errorMessage: "",
        };
    },
    watch: {
        form: {
            handler(newForm) {
                this.$emit("update:formData", newForm);
            },
            deep: true,
        },
    },
    mounted() {
        document.body.style.overflow = "hidden";
    },
    beforeDestroy() {
        document.body.style.overflow = "";
    },
    methods: {
        close() {
            this.$emit("close");
        },
        async handleSubmit() {
            this.submitting = true;

            try {
                const user = this.$state.user.user;
                const userName = user?.name || this.$tc("support.defaultUserName");

                await deskProService.createTicket({
                    name: userName,
                    email: this.form.email,
                    subject: this.$tc("support.defaultSubject"),
                    message: this.form.message,
                });

                this.submissionSuccess = true;
                this.submitted = true;
            } catch (error) {
                console.error("Failed to submit support ticket:", error);
                this.submissionSuccess = false;
                this.submitted = true;
                const apiErrorMessage = error instanceof Error ? error.message : this.$tc("support.errorMessage");
                this.errorMessage = `${this.$tc("support.reason")} ${apiErrorMessage}`;
            } finally {
                this.submitting = false;
            }
        },
    },
});
</script>

<style scoped lang="scss">
@use "src/scss/variables";

.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.6);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: #{variables.$z-index-support-btn + 1};
    padding: 10px;
}

.modal-container {
    background: white;
    border-radius: 8px;
    max-width: 600px;
    width: 100%;
    max-height: 90vh;
    overflow-y: auto;
    position: relative;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
}

.modal-close {
    position: absolute;
    top: 16px;
    right: 12px;
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px;
    z-index: 10;
    opacity: 0.6;
    transition: opacity 0.2s;

    &:hover {
        opacity: 1;
    }

    i {
        font-size: 18px;
        width: 18px;
        height: 18px;
        color: var(--color-dark);
    }
}

.modal-back {
    position: absolute;
    top: 16px;
    left: 16px;
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px 8px;
    z-index: 10;
    color: var(--color-dark);
    opacity: 0.6;
    transition: opacity 0.2s;
    font-size: 14px;

    &:hover {
        opacity: 1;
    }
}

.modal-content {
    padding: 40px;

    @media (max-width: 600px) {
        padding: 30px 20px;
    }
}

.form-header {
    margin-bottom: 30px;

    h2 {
        margin: 0 0 10px 0;
        font-size: 24px;
        font-weight: 600;
    }

    .form-description {
        margin: 0;
        color: #666;
        font-size: 14px;
        line-height: 1.5;
    }
}

.form-group {
    margin-bottom: 20px;

    label {
        display: block;
        text-align: left;
        margin-bottom: 8px;
        font-size: 14px;
    }

    input,
    textarea {
        width: 100%;
        padding: 10px 12px;
        border: 1px solid #ddd;
        border-radius: 4px;
        font-size: 14px;
        transition: border-color 0.2s;
        color: var(--color-dark);
        box-sizing: border-box;

        &:focus {
            outline: none;
            border-color: var(--color-primary);
        }

        &:disabled {
            background-color: #f5f5f5;
            cursor: not-allowed;
        }
    }

    textarea {
        resize: none;
        font-family: inherit;
    }
}

.form-actions {
    margin-top: 30px;
}

.btn {
    padding: 12px 32px;
}

.result-screen {
    padding: 60px 40px;
    text-align: center;

    @media (max-width: 600px) {
        padding: 50px 30px;
    }
}

.result-icon {
    width: 80px;
    height: 80px;
    margin: 0 auto 8px;
    display: flex;
    align-items: center;
    justify-content: center;

    .icon {
        font-size: 60px;
    }
}

.result-title {
    margin: 0 0 20px 0;
    font-size: 24px;
    font-weight: 600;
    color: #2c3e50;
}

.result-message {
    margin: 0;
    color: #666;
    font-size: 16px;
    line-height: 1.6;
}
</style>
